import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { Button, Dialog } from './primitives';

// Fase 24B: reemplazo accesible y consistente de window.confirm. `useConfirm()` devuelve una funcion
// `confirm(options) => Promise<boolean>` que resuelve true SOLO si el usuario confirma — misma semantica
// que window.confirm (la accion se ejecuta iff true). No cambia ninguna llamada/permiso/payload.
// P-075: con la piel del rediseño (`Dialog` y `Button`). Una acción destructiva es `danger` (en rojo, sin
// caja: el rediseño no tiene botón rojo sólido); la que no lo es, `primary`. Confirmar sigue con el foco.

export interface ConfirmOptions {
  title?: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface ConfirmState extends ConfirmOptions {
  open: boolean;
  busy: boolean;
}

const ConfirmContext = createContext<((options: ConfirmOptions) => Promise<boolean>) | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConfirmState>({ open: false, busy: false, description: '' });
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setState({ open: true, busy: false, ...options });
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState((s) => ({ ...s, open: false }));
    if (resolve) resolve(value);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state.open && (
        <Dialog
          title={state.title ?? 'Confirmar'}
          onClose={() => settle(false)}
          footer={
            <>
              <Button onClick={() => settle(false)} variant="secondary">
                {state.cancelLabel ?? 'Cancelar'}
              </Button>
              <Button autoFocus onClick={() => settle(true)} variant={state.danger ? 'danger' : 'primary'}>
                {state.confirmLabel ?? (state.danger ? 'Eliminar' : 'Confirmar')}
              </Button>
            </>
          }
        >
          <p className="n3-confirm__text">{state.description}</p>
        </Dialog>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): (options: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm debe usarse dentro de <ConfirmProvider>');
  return ctx;
}
